import { type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { Button } from './Button'
import { Icon } from './Icon'
import { ViewToggle } from './ViewToggle'
import { useView } from '../views/ViewContext'

/**
 * Shared brand chrome — ported from `FgNavbar` in `designs/system.jsx` (~386),
 * itself derived from nodes `857:956` (Local) and `713:2293` (Tourism).
 *
 * One component, not two: same geometry and position on both surfaces, and only
 * the trailing slot differs. Upstream geometry is a floating pill — bg-soft,
 * radius 30, padding 8/32 — rather than the full-bleed bordered bar the app
 * shipped before this.
 *
 * The app had no shared navbar at all: four pages hand-rolled a brand header
 * and thirteen more hand-roll a back-bar. Only the brand header is chrome; the
 * back-bars are in-flow navigation and belong to their own page tickets.
 */
export function Navbar({
  tagline,
  className,
}: {
  /** Optional strapline under the wordmark. Local ships one ("Find a hospital,
   *  fast."); the frames draw none. It is carried as a prop rather than dropped
   *  because removing shipped copy is a content decision, and M17 changes how
   *  things look, not what they say. */
  tagline?: string
  className?: string
}): ReactNode {
  const { view } = useView()
  const navigate = useNavigate()

  return (
    <header
      className={[
        // PORTED — 857:956: pill, bg-soft, radius 30, padding 8/32. Wraps to two
        // rows below sm, matching the mobile frame (19:256), which carries the
        // toggle on its own row rather than squeezing three items into 390pt.
        'mx-3 mt-3 flex flex-wrap items-center justify-between gap-x-8 gap-y-2 rounded-[30px] bg-bg-soft px-4 py-2 sm:px-8',
        className ?? '',
      ].join(' ')}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-r3 bg-accent text-accent-on"
          aria-hidden="true"
        >
          <Icon name="bed" size={16} />
        </span>
        <div>
          <span className="block text-[20px] font-medium leading-7 text-accent-ink">
            {/* THE TLD IS GONE ON PURPOSE. The wordmark used to read
                `medairbnb.gh`, which welded the country into the logo — and the
                network will not stay inside Ghana (owner, 2026-09-12). The
                two-tone lockup is kept by splitting the name itself, so the
                mark still has its accent without claiming a territory. */}
            med<span className="text-accent">cor</span>
          </span>
          {tagline !== undefined && (
            <p className="text-[11px] leading-none text-ink-3">{tagline}</p>
          )}
        </div>
      </div>

      {/* Centred once the bar wraps, right-aligned when it does not: the mobile
          frame (19:256) gives the toggle its own centred row rather than
          hanging it off the right edge. */}
      <div className="flex flex-1 items-center justify-center gap-3 sm:flex-none sm:justify-end">
        <ViewToggle />
        {/* The Local CTA is a DELIBERATE OMISSION. The frame puts an Emergency
            button here (857:956); the app routes to /emergency from the home
            Emergency tile instead.

            This omission used to be justified by `SOSBar` — a --sos band on
            every Local screen, said to be louder than a pill button. ADR-0042
            deleted that bar, and the reason it gives matters here: the bar was
            never wired. All thirteen call sites rendered it with no handler, so
            it absorbed taps and did nothing. An omission cannot rest on a
            control that does not work, so the justification is restated.

            The omission still stands, on narrower ground: this Navbar renders on
            the home, Tourism and the legal/contact pages, and the home already
            carries the Emergency tile. Adding a button here would duplicate that
            tile and would not reach the twelve deep screens that have no path at
            all (ADR-0042 accepts that cost explicitly). Adding one is a flow
            change either way, and ADR-0038 stops Figma's authority at the visual
            language.

            Tourism's CTA has no such conflict and is ported. It takes the brand
            accent, not --sos: the frame paints it crimson (713:2293), but
            tokens.css reserves full-saturation red for emergency surfaces, and
            spending it on a marketing CTA dilutes the one signal that must
            never be ambiguous. That departure was made in M16-T13 and carries
            over unchanged. */}
        {view === 'tourism' && (
          <Button
            className="hidden sm:inline-flex"
            onClick={() => navigate('/tourism/enquiry')}
          >
            Request Consultation
          </Button>
        )}
      </div>
    </header>
  )
}
